
import { memo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { MultiSelect } from '@/components/ui/multi-select'; // Removed unused import
import { Sparkles, HelpCircle } from 'lucide-react';

export const SmartImputeConfig = memo(({ data, onChange, inputColumns }: any) => {


    return (
        <div className="space-y-6">
            <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Smart Impute
                </h4>
                <p>Automatically fill missing values using statistical strategies.</p>
            </div>

            {/* Imputation Strategy */}
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Strategy</Label>
                <Select
                    value={data.strategy || 'mean'}
                    onValueChange={(val) => onChange('strategy', val)}
                >
                    <SelectTrigger className="w-full text-xs">
                        <SelectValue placeholder="Select Strategy" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="mean">Mean (Average)</SelectItem>
                        <SelectItem value="median">Median (Middle Value)</SelectItem>
                        <SelectItem value="mode">Mode (Most Frequent)</SelectItem>
                        <SelectItem value="constant">Constant Value</SelectItem>
                        <SelectItem value="linear">Linear Interpolation</SelectItem>
                        <SelectItem value="ffill">Forward Fill</SelectItem>
                        <SelectItem value="bfill">Backward Fill</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Target Columns */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Target Columns</Label>
                </div>
                {/* Fallback to simple multi-select mock if component missing, but using standard select for single for now or assume MultiSelect from ui */}
                {/* If MultiSelect doesn't exist, we can use a simple list of checkboxes or just a comma separated input for now to be safe, 
                     but standard PropertiesPanel uses standard HTML inputs usually. Let's use a multi-select simulation or just a text area for "All / Specific" 
                 */}
                <div className="text-xs text-gray-500 mb-1">Select columns to apply imputation to.</div>
                <select
                    multiple
                    className="w-full border rounded p-2 text-xs min-h-[100px]"
                    value={data.targetColumns || data.columns || []}
                    onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        onChange('targetColumns', selected);
                    }}
                >
                    {inputColumns.map((col: any) => (
                        <option key={col.name} value={col.name}>{col.name} ({col.type})</option>
                    ))}
                </select>
            </div>

            {/* Fill Value (Conditional) */}
            {data.strategy === 'constant' && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Constant Value</Label>
                    <Input
                        className="text-xs"
                        placeholder="e.g. 0, Unknown"
                        value={data.constantValue ?? data.fillValue ?? ''}
                        onChange={(e) => onChange('constantValue', e.target.value)}
                    />
                </div>
            )}

            <div className="space-y-3">
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={data.addMissingIndicator === true} onChange={(e) => onChange('addMissingIndicator', e.target.checked)} className="rounded" />
                    <span className="text-xs">Add missing indicator column</span>
                </label>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Group By (optional)</Label>
                <select
                    className="w-full border rounded p-2 text-xs"
                    value={data.groupBy || ''}
                    onChange={(e) => onChange('groupBy', e.target.value || null)}
                >
                    <option value="">— None —</option>
                    {inputColumns.map((col: any) => <option key={col.name} value={col.name}>{col.name}</option>)}
                </select>
            </div>

            <div className="flex gap-2 items-start bg-blue-50 p-2 rounded text-[10px] text-blue-700">
                <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>
                    <strong>Tip:</strong> Use "Mean" or "Median" for numeric fields. Use "Mode" or "Constant" for categorical text fields.
                </span>
            </div>
        </div>
    );
});
